import { formatarDataHora as quando } from '@common/i18n/formatosData';
import { Alert, Collapsible, IconButton } from '@ntconsult/aluma-react';
import { ChevronDown, CircleCheck } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUltimaSincronizacao } from '../hooks';
import type { UltimaSincronizacao } from '../types/Sincronizacao';

const CONTAGENS = ['incluidos', 'alterados', 'reativados', 'excluidos', 'ignorados'] as const;

type Ultima = NonNullable<UltimaSincronizacao['ultima']>;

function Contagens({ ultima }: { ultima: Ultima }) {
  const { t } = useTranslation('participantes');
  return (
    <dl className="grid max-w-xl grid-cols-1 gap-y-1 text-sm sm:grid-cols-[14rem_1fr]">
      {CONTAGENS.map((c) => (
        <div key={c} className="contents">
          <dt className="text-foreground-muted">{t(`contagens.${c}`)}</dt>
          <dd>{ultima[c]}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Sempre começa fechado (spec 34 R11); remontado a cada sincronização. */
function Detalhes({ ultima, naCampanha }: { ultima: Ultima; naCampanha: number }) {
  const { t } = useTranslation('participantes');
  const [aberto, setAberto] = useState(false);
  return (
    <Collapsible open={aberto} onOpenChange={setAberto} className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <CircleCheck aria-hidden className="size-4 text-success" />
        <span data-status-sincronizacao>
          {t('status.sincronizada', { quando: quando(ultima.concluidaEm), count: naCampanha })}
        </span>
        {/* Seta no lugar do texto "Detalhes" (pedido do Lorenzo em 2026-10-25); o texto fica no nome. */}
        <IconButton
          size="sm"
          label={t('status.detalhes')}
          title={t('status.detalhes')}
          aria-expanded={aberto}
          icon={
            <ChevronDown
              aria-hidden
              className={`transition-transform ${aberto ? 'rotate-180' : ''}`}
            />
          }
          onClick={() => setAberto((a) => !a)}
        />
      </div>
      <Collapsible.Content forceMount={false}>
        <Contagens ultima={ultima} />
      </Collapsible.Content>
    </Collapsible>
  );
}

/** Linha de status da sincronização acima da lista (spec 28 R20, spec 36 R5 e R6); some se nunca sincronizou. */
export function SincronizacaoStatus({
  campanhaId,
  naCampanha,
}: {
  campanhaId: string;
  naCampanha: number;
}) {
  const { t } = useTranslation('participantes');
  const ultima = useUltimaSincronizacao(campanhaId).data?.ultima;
  if (!ultima) return null;
  if (ultima.resultado === 'falha')
    return (
      <Alert variant="danger" title={ultima.erro ?? t('falhou')}>
        <Contagens ultima={ultima} />
      </Alert>
    );
  return <Detalhes key={ultima.id} ultima={ultima} naCampanha={naCampanha} />;
}
